// Renderer de rutas OSRM en MapLibre.
// Maneja 2 sources + 4 layers con API de alto nivel. Independiente del
// unified source del mapa — las rutas viven en sources dedicados para no
// interferir con el pipeline de entidades.
//
// Consumido por: views/MapV2.vue
// Dependencias: osrm-constants.js

import maplibregl from 'maplibre-gl'
import {
  OSRM_PREVIEW_SOURCE, OSRM_ACTIVE_SOURCE,
  OSRM_PREVIEW_LAYER, OSRM_ACTIVE_LINE_OUTLINE, OSRM_ACTIVE_LINE, OSRM_ACTIVE_TARGET,
  REROUTE_THRESHOLD_M, PREVIEW_COLOR, ACTIVE_COLOR, ACTIVE_OUTLINE_COLOR,
  PREVIEW_WIDTH, ACTIVE_WIDTH, ACTIVE_OUTLINE_WIDTH,
} from '../utils/osrm-constants'

// Helpers de formateo para el badge "X km · Y min" sobre el mapa.
function formatDistance(m) {
  if (m == null) return ''
  const km = m / 1000
  return km >= 10 ? `${km.toFixed(0)} km` : `${km.toFixed(1)} km`
}
function formatDuration(s) {
  if (s == null) return ''
  const totalMin = Math.round(s / 60)
  if (totalMin < 1) return '< 1 min'
  if (totalMin < 60) return `${totalMin} min`
  const hh = Math.floor(totalMin / 60)
  const mm = totalMin % 60
  return `${hh} h${mm ? ' ' + mm + ' min' : ''}`
}

const EMPTY_FC = { type: 'FeatureCollection', features: [] }

function RouteRenderer(map, callbacks) {
  this.map = map
  this.callbacks = callbacks || {}

  // Estado de la ruta activa (persistente al clic, reroute >300m)
  // null cuando no hay ruta activa.
  this.active = null
  // { entityId, originLngLat, destLngLat, destEntityType, geometry, distance, duration }

  this._initSources()
  this._initLayers()
}

// ─── Inicialización ──────────────────────────────────────

RouteRenderer.prototype._initSources = function () {
  if (!this.map.getSource(OSRM_PREVIEW_SOURCE)) {
    this.map.addSource(OSRM_PREVIEW_SOURCE, { type: 'geojson', data: EMPTY_FC })
  }
  if (!this.map.getSource(OSRM_ACTIVE_SOURCE)) {
    this.map.addSource(OSRM_ACTIVE_SOURCE, { type: 'geojson', data: EMPTY_FC })
  }
}

RouteRenderer.prototype._initLayers = function () {
  const beforeId = this._getFirstEntityLayerId()

  // Preview (dashed amarillo) — queda debajo de la ruta activa
  this.map.addLayer({
    id: OSRM_PREVIEW_LAYER,
    type: 'line',
    source: OSRM_PREVIEW_SOURCE,
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: {
      'line-color': PREVIEW_COLOR(),
      'line-width': PREVIEW_WIDTH(),
      'line-opacity': 0.6,
      'line-dasharray': [3, 2],
    },
  }, beforeId)

  // Outline blanco debajo de la línea activa. Width escala con la activa
  // para no perder el efecto de halo al subir el ancho en tactical.
  this.map.addLayer({
    id: OSRM_ACTIVE_LINE_OUTLINE,
    type: 'line',
    source: OSRM_ACTIVE_SOURCE,
    filter: ['==', ['geometry-type'], 'LineString'],
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: {
      'line-color': ACTIVE_OUTLINE_COLOR,
      'line-width': ACTIVE_OUTLINE_WIDTH(),
      'line-opacity': 0.9,
    },
  }, beforeId)

  // Línea activa naranja-rojo (width gobernado por tema).
  this.map.addLayer({
    id: OSRM_ACTIVE_LINE,
    type: 'line',
    source: OSRM_ACTIVE_SOURCE,
    filter: ['==', ['geometry-type'], 'LineString'],
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: {
      'line-color': ACTIVE_COLOR(),
      'line-width': ACTIVE_WIDTH(),
      'line-opacity': 0.95,
    },
  }, beforeId)

  // Círculo en el punto destino con halo blanco
  this.map.addLayer({
    id: OSRM_ACTIVE_TARGET,
    type: 'circle',
    source: OSRM_ACTIVE_SOURCE,
    filter: ['==', ['geometry-type'], 'Point'],
    paint: {
      'circle-radius': 8,
      'circle-color': ACTIVE_COLOR(),
      'circle-stroke-color': '#FFFFFF',
      'circle-stroke-width': 2,
      'circle-opacity': 0.9,
    },
  }, beforeId)
}

RouteRenderer.prototype._getFirstEntityLayerId = function () {
  const style = this.map.getStyle()
  if (!style || !style.layers) return undefined
  const preferredOrder = ['unified-static-markers', 'unified-clusters', 'unified-dynamic-markers']
  for (let i = 0; i < preferredOrder.length; i++) {
    const id = preferredOrder[i]
    const found = style.layers.find(l => l.id === id)
    if (found) return found.id
  }
  const layer = style.layers.find((l) => { return l.source === 'unified-source' })
  return layer ? layer.id : undefined
}

// ─── API pública: preview (hover) ─────────────────────────

RouteRenderer.prototype.showPreview = function (geometry) {
  if (!geometry) {
    this.clearPreview()
    return
  }
  const source = this.map.getSource(OSRM_PREVIEW_SOURCE)
  if (!source) return
  source.setData({
    type: 'FeatureCollection',
    features: [{ type: 'Feature', geometry, properties: {} }],
  })
}

RouteRenderer.prototype.showPreviewStraight = function (origin, destination) {
  const source = this.map.getSource(OSRM_PREVIEW_SOURCE)
  if (!source) return
  source.setData({
    type: 'FeatureCollection',
    features: [{
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: [origin, destination] },
      properties: { fallback: true },
    }],
  })
}

RouteRenderer.prototype.clearPreview = function () {
  const source = this.map.getSource(OSRM_PREVIEW_SOURCE)
  if (source) source.setData(EMPTY_FC)
}

// ─── API pública: ruta activa (clic persistente) ─────────

RouteRenderer.prototype.showActive = function (params) {
  this.active = { ...params }
  const fc = {
    type: 'FeatureCollection',
    features: [
      { type: 'Feature', geometry: params.geometry, properties: { kind: 'line' } },
      { type: 'Feature', geometry: { type: 'Point', coordinates: params.destLngLat }, properties: { kind: 'target' } },
    ],
  }
  const source = this.map.getSource(OSRM_ACTIVE_SOURCE)
  if (source) source.setData(fc)
  // Badge "X km · Y min" anclado al origen (unidad). Se reposiciona y
  // actualiza el texto en cada showActive (incluyendo los re-routings
  // cada vez que la unidad se mueve >100m).
  this._updateInfoBadge(params)
}

RouteRenderer.prototype.clearActive = function () {
  this.active = null
  const source = this.map.getSource(OSRM_ACTIVE_SOURCE)
  if (source) source.setData(EMPTY_FC)
  this._removeInfoBadge()
}

// ─── Badge de info (km + ETA) anclado a la unidad ──────────

RouteRenderer.prototype._updateInfoBadge = function (params) {
  if (!params || !params.originLngLat || params.distance == null || params.duration == null) {
    this._removeInfoBadge()
    return
  }
  const distText = formatDistance(params.distance)
  const durText = formatDuration(params.duration)
  if (!this._infoBadgeEl) {
    const el = document.createElement('div')
    el.className = 'osrm-route-info-badge'
    // El asterisco indica que el tiempo es OSRM "free flow" (sin tráfico).
    // Al hover muestra la nota completa.
    el.title = 'Tiempo estimado sin tráfico (calculado por OSRM con velocidades teóricas por tipo de vía)'
    const dist = document.createElement('span')
    dist.className = 'osrm-route-info-badge__dist'
    const sep = document.createElement('span')
    sep.className = 'osrm-route-info-badge__sep'
    sep.textContent = '·'
    const time = document.createElement('span')
    time.className = 'osrm-route-info-badge__time'
    const star = document.createElement('span')
    star.className = 'osrm-route-info-badge__star'
    star.textContent = '*'
    el.appendChild(dist)
    el.appendChild(sep)
    el.appendChild(time)
    el.appendChild(star)
    this._infoBadgeEl = el
    this._infoBadgeDistEl = dist
    this._infoBadgeTimeEl = time
  }
  this._infoBadgeDistEl.textContent = distText
  this._infoBadgeTimeEl.textContent = durText
  if (!this._infoBadgeMarker) {
    this._infoBadgeMarker = new maplibregl.Marker({
      element: this._infoBadgeEl,
      anchor: 'bottom',
      offset: [0, -20], // sobre el ícono del device
    }).setLngLat(params.originLngLat).addTo(this.map)
  } else {
    this._infoBadgeMarker.setLngLat(params.originLngLat)
  }
}

RouteRenderer.prototype._removeInfoBadge = function () {
  if (this._infoBadgeMarker) {
    this._infoBadgeMarker.remove()
    this._infoBadgeMarker = null
  }
  this._infoBadgeEl = null
  this._infoBadgeDistEl = null
  this._infoBadgeTimeEl = null
}

RouteRenderer.prototype.checkReroute = function (entityId, currentLngLat) {
  if (!this.active || this.active.entityId !== entityId) return false
  if (!Array.isArray(currentLngLat) || currentLngLat.length < 2) return false
  const d = this._haversineMeters(this.active.originLngLat, currentLngLat)
  if (d < REROUTE_THRESHOLD_M) return false
  if (this.callbacks.onRerouteNeeded) this.callbacks.onRerouteNeeded(entityId)
  return true
}

RouteRenderer.prototype.destroy = function () {
  const layers = [OSRM_ACTIVE_TARGET, OSRM_ACTIVE_LINE, OSRM_ACTIVE_LINE_OUTLINE, OSRM_PREVIEW_LAYER]
  for (let i = 0; i < layers.length; i++) {
    if (this.map && this.map.getLayer(layers[i])) this.map.removeLayer(layers[i])
  }
  const sources = [OSRM_ACTIVE_SOURCE, OSRM_PREVIEW_SOURCE]
  for (let j = 0; j < sources.length; j++) {
    if (this.map && this.map.getSource(sources[j])) this.map.removeSource(sources[j])
  }
}

// ─── Utilidades ─────────────────────────────────────────

RouteRenderer.prototype._haversineMeters = function (a, b) {
  const R = 6371000
  const toRad = d => (d * Math.PI) / 180
  const dLat = toRad(b[1] - a[1])
  const dLng = toRad(b[0] - a[0])
  const lat1 = toRad(a[1])
  const lat2 = toRad(b[1])
  const sinDLat = Math.sin(dLat / 2)
  const sinDLng = Math.sin(dLng / 2)
  const h = (sinDLat * sinDLat) + (Math.cos(lat1) * Math.cos(lat2) * sinDLng * sinDLng)
  return 2 * R * Math.asin(Math.sqrt(h))
}

export default RouteRenderer
